import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import type { MedicationCatalogItem, MedicationCatalogUpdate } from '../../../types/medication';

export const MedicationCatalogItemDrawer: React.FC<{
  isOpen: boolean;
  item: MedicationCatalogItem;
  onClose: () => void;
  onSave: (data: MedicationCatalogUpdate) => Promise<void>;
}> = ({ isOpen, item, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [defaultDose, setDefaultDose] = useState('');
  const [doseUnit, setDoseUnit] = useState('');
  const [frequency, setFrequency] = useState('');
  const [defaultQty, setDefaultQty] = useState<string>('');
  const [defaultRefills, setDefaultRefills] = useState<string>('');
  const [durationDays, setDurationDays] = useState<string>('');
  const [patientInstructions, setPatientInstructions] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [controlled, setControlled] = useState(false);
  const [stockTracked, setStockTracked] = useState(false);
  const [saving, setSaving] = useState(false);

  useEscapeKey(onClose, isOpen);

  useEffect(() => {
    if (!isOpen) return;
    setName(item.name || '');
    setDefaultDose(item.default_dose || '');
    setDoseUnit(item.default_dose_unit || '');
    setFrequency(item.default_frequency || '');
    setDefaultQty(item.default_quantity != null ? String(item.default_quantity) : '');
    setDefaultRefills(item.default_refills != null ? String(item.default_refills) : '');
    setDurationDays(item.default_duration_days != null ? String(item.default_duration_days) : '');
    setPatientInstructions(item.instructions || '');
    setInternalNotes(item.notes || '');
    setControlled(Boolean(item.is_controlled_substance));
    setStockTracked(Boolean(item.is_stock_tracked));
  }, [isOpen, item.id]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const payload: MedicationCatalogUpdate = {
        name: name.trim(),
        default_dose: defaultDose || undefined,
        default_dose_unit: doseUnit || undefined,
        default_frequency: frequency || undefined,
        default_quantity: defaultQty === '' ? undefined : Number(defaultQty),
        default_refills: defaultRefills === '' ? undefined : Number(defaultRefills),
        default_duration_days: durationDays === '' ? undefined : Number(durationDays),
        instructions: patientInstructions || undefined,
        notes: internalNotes || undefined,
        is_controlled_substance: controlled,
        is_stock_tracked: stockTracked,
      };
      await onSave(payload);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl border-l border-gray-200 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">Update medication</div>
            <div className="text-xs text-gray-500 mt-0.5 truncate">Update medication details and inventory settings.</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-gray-100 text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Medication Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Dose</label>
              <input
                value={defaultDose}
                onChange={(e) => setDefaultDose(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dose Unit</label>
              <input
                value={doseUnit}
                onChange={(e) => setDoseUnit(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Frequency</label>
            <input
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Qty</label>
              <input
                value={defaultQty}
                onChange={(e) => setDefaultQty(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Refills</label>
              <input
                value={defaultRefills}
                onChange={(e) => setDefaultRefills(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration (days)</label>
              <input
                value={durationDays}
                onChange={(e) => setDurationDays(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Patient Instructions</label>
            <textarea
              value={patientInstructions}
              onChange={(e) => setPatientInstructions(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Internal Notes</label>
            <textarea
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center gap-3 pt-1">
            <input
              type="checkbox"
              checked={controlled}
              onChange={(e) => setControlled(e.target.checked)}
              className="w-4 h-4"
            />
            <div className="text-sm text-gray-900">Controlled substance</div>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={() => setStockTracked((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                stockTracked ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  stockTracked ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <div>
              <div className="text-sm font-medium text-gray-900">Track as inventory</div>
              <div className="text-xs text-gray-500">Enables on-hand, lots, and inventory reporting.</div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};



